import './PaySheet.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PaySheetProps } from './PaySheet.interface';
import { imActions, imSelector } from '$store/im';
import { useCurrentUserInfo, useTranslator } from '$hooks';
import { roundCryptoNumber, roundNumber, statReachGoal } from '$utils';
import { mainSelector } from '$store/main';
import {
  Button,
  CurrencyIcon,
  List,
  ListItem,
  PaymentMethodIcon,
  Placeholder,
} from '$uikit';
import { Loader } from '$shared/components';
import { payActions, paySelector } from '$store/pay';
import { PayMethod } from '$store/pay/interface';
import { IcAngleRight16, IcChevron16, IcPlusCircle28 } from '$assets';
import { BottomSheet } from '$uikit/BottomSheet/BottomSheet';

const PaySheetInner: FC<PaySheetProps> = (props) => {
  const dispatch = useDispatch();
  const { card } = useSelector(mainSelector);
  const {
    isActive,
    title,
    description,
    availableMethods,
    availableCurrencies,
    amount,
    loadingRates,
    rates,
    payUrl,
  } = useSelector(paySelector);
  const { tonRate } = useSelector(imSelector);
  const t = useTranslator();
  const user = useCurrentUserInfo();
  const [selectCurrency, setSelectCurrency] = useState(false);

  useEffect(() => {
    statReachGoal('pay_sheet_opened');
  }, []);

  useEffect(() => {
    isActive && dispatch(imActions.loadTonRate());
  }, [isActive]);

  const isPayUrlToTonkeeper = useMemo(() => {
    return payUrl && payUrl.includes('tonkeeper');
  }, [payUrl]);

  const handleTonkeeper = useCallback(() => {
    dispatch(payActions.chooseMethod({ method: PayMethod.TONKEEPER }));
    statReachGoal('pay_sheet_tonkeeper_selected');
  }, []);

  const handleBalance = useCallback(() => {
    dispatch(payActions.chooseMethod({ method: PayMethod.BALANCE }));
    statReachGoal('pay_sheet_balance_selected');
  }, []);

  const handleCard = useCallback(() => {
    dispatch(payActions.chooseMethod({ method: PayMethod.CARD }));
    statReachGoal('pay_sheet_card_selected', {
      amount,
    });
  }, [amount]);

  const handleClose = useCallback(() => {
    dispatch(payActions.cancel());
    setSelectCurrency(false);
    dispatch(payActions.setPayUrl(''));
  }, [dispatch]);

  const handleAddNewCard = useCallback(() => {
    dispatch(
      payActions.chooseMethod({
        method: PayMethod.CARD,
        options: {
          skipAutoPay: true,
        },
      }),
    );
  }, []);

  const handleChangeCryptobot = useCallback(() => {
    setSelectCurrency(true);
    dispatch(payActions.getRates());
  }, [dispatch]);

  const eurPrice = useMemo(() => {
    if (tonRate) {
      const res = amount * tonRate;
      if (res < 1) {
        return 1;
      }

      return roundNumber(res, 2);
    } else {
      return 0;
    }
  }, [amount, tonRate]);

  function renderContent() {
    if (!tonRate || (selectCurrency && loadingRates)) {
      return (
        <Placeholder>
          <Loader />
        </Placeholder>
      );
    }

    if (payUrl) {
      if (isPayUrlToTonkeeper) {
        return (
          <Placeholder
            title={t('subscription_link_generated_title')}
            actions={
              <Button href={payUrl} target="_blank">
                {t('subscription_link_generated_button')}
              </Button>
            }
          >
            {t('subscription_link_generated')}
          </Placeholder>
        );
      }

      return (
        <Placeholder
          actions={
            <a target="_blank" rel="noreferrer" href={payUrl}>
              <Button>{t('payment_cryptopay_pay_button')}</Button>
            </a>
          }
        >
          {t('payment_cryptopay_invoice_caption')}
        </Placeholder>
      );
    }

    if (selectCurrency) {
      return (
        <div className="PaySheet__selectCurrency">
          <div>{t('payment_cryptopay_caption')}</div>
          <List>
            {availableCurrencies.map((c) => (
              <ListItem
                key={c}
                icon={<IcChevron16 />}
                iconSize="small"
                onClick={() =>
                  dispatch(
                    payActions.chooseMethod({
                      method: PayMethod.CRYPTOPAY,
                      options: { currency: c },
                    }),
                  )
                }
                before={<CurrencyIcon currency={c} />}
              >
                {roundCryptoNumber(rates[c.toLowerCase()] * amount)} {c}
              </ListItem>
            ))}
          </List>

          <List>
            <ListItem onClick={() => setSelectCurrency(false)}>{t('back')}</ListItem>
          </List>
        </div>
      );
    }

    return (
      <div className="PaySheet__cont">
        <div className="PaySheet__info">{description}</div>
        {availableMethods.includes(PayMethod.CARD) &&
          (card ? (
            <div className="PaySheet__item">
              <div className="PaySheet__method" onClick={handleCard}>
                <div className="PaySheet__method__icon">
                  <PaymentMethodIcon method={card.brand} />
                </div>
                <div className="PaySheet__method__cont">
                  <div className="PaySheet__method__title">
                    {t('payment_method_card_exit_pref')} •••• {card.last4}
                  </div>
                  <div className="PaySheet__method__caption">~{eurPrice} EUR</div>
                </div>
                <div className="PaySheet__method__chevron">
                  <IcAngleRight16 />
                </div>
              </div>
              <div className="PaySheet__addCard" onClick={handleAddNewCard}>
                <IcPlusCircle28 />
                <div className="PaySheet__addCard__caption">
                  {t('payment_add_new_card')}
                </div>
              </div>
            </div>
          ) : (
            <div className="PaySheet__item">
              <div className="PaySheet__method" onClick={handleCard}>
                <div className="PaySheet__method__icon">
                  <PaymentMethodIcon method="card" />
                </div>
                <div className="PaySheet__method__cont">
                  <div className="PaySheet__method__title">
                    {t('payment_method_card')}
                  </div>
                  <div className="PaySheet__method__caption">~{eurPrice} EUR</div>
                </div>
                <div className="PaySheet__method__chevron">
                  <IcAngleRight16 />
                </div>
              </div>
            </div>
          ))}
        {availableMethods.includes(PayMethod.CARD) && (
          <div className="PaySheet__other_methods">
            {t('payment_other_methods_title')}
          </div>
        )}
        <List>
          {availableMethods.includes(PayMethod.TONKEEPER) && (
            <ListItem
              onClick={handleTonkeeper}
              indicator={`${amount} TON / ${t('subscription_period')}`}
            >
              {t('payment_method_tonkeeper')}
            </ListItem>
          )}
          {availableMethods.includes(PayMethod.BALANCE) && (
            <ListItem onClick={handleBalance} indicator={`${amount} TON`}>
              {t('payment_method_balance')}
            </ListItem>
          )}
          {availableMethods.includes(PayMethod.CRYPTOPAY) && (
            <ListItem onClick={handleChangeCryptobot} chevron>
              {t('payment_method_cryptopay')}
            </ListItem>
          )}
        </List>
      </div>
    );
  }

  if (isActive) {
    return (
      <BottomSheet title={title} onClose={handleClose}>
        {renderContent()}
      </BottomSheet>
    );
  } else {
    return null;
  }
};

export const PaySheet: FC = () => {
  const { isActive } = useSelector(paySelector);

  if (!isActive) {
    return null;
  }

  return <PaySheetInner />;
};
